
import React, { useEffect } from "react";
import { ChevronUp, Zap } from "lucide-react";
import { Spinner, CLS } from "../constants";

const Step2SelectPages = ({
  pageCount,
  maxSelectablePages,
  selectedPages,
  onSelectedPagesChange,
  onSubmit,
  isSubmitting,
  error,
  onCancel,
}) => {
  // Auto-skip if all pages fit within max selectable
  useEffect(() => {
    if (pageCount != null && maxSelectablePages != null && pageCount <= maxSelectablePages) {
      // Auto-submit with empty selection (backend auto-selects all)
      onSubmit();
    }
  }, [pageCount, maxSelectablePages, onSubmit]);

  // If skipping, show minimal UI
  if (pageCount == null || maxSelectablePages == null) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#FEF9F7]">
        <Spinner size={20} />
      </div>
    );
  }

  if (pageCount <= maxSelectablePages) {
    return (
      <div className="flex-1 flex items-center justify-center px-6 bg-[#FEF9F7]">
        <div className="text-center">
          <Spinner size={18} className="mx-auto mb-3" />
          <p className="text-[0.8125rem] text-[#71717A]">
            Preparing to build template…
          </p>
        </div>
      </div>
    );
  }

  // Large document: show page picker
  const canSelectMore = selectedPages.length < maxSelectablePages;
  const allPages = Array.from({ length: pageCount }, (_, i) => i + 1);

  const handleTogglePage = (pageNum) => {
    if (selectedPages.includes(pageNum)) {
      onSelectedPagesChange(selectedPages.filter((p) => p !== pageNum));
    } else if (selectedPages.length < maxSelectablePages) {
      onSelectedPagesChange([...selectedPages, pageNum]);
    }
  };

  const handleSelectAll = () => {
    onSelectedPagesChange(allPages.slice(0, maxSelectablePages));
  };

  const handleClearAll = () => {
    onSelectedPagesChange([]);
  };

  const canSubmit = selectedPages.length === maxSelectablePages && !isSubmitting;

  return (
    <div className="flex-1 overflow-y-auto flex justify-center px-6 pt-8 pb-12 bg-[#FEF9F7]">
      <div className="w-full max-w-[640px] flex flex-col gap-5">

        {/* Title */}
        <div>
          <h2 className="text-base font-bold text-[#171717] m-0 mb-1">
            Select representative pages
          </h2>
          <p className="text-[0.8125rem] text-[#71717A] m-0 leading-[1.6]">
            Your document has <strong>{pageCount} pages</strong>. AI works best on a sample of <strong>{maxSelectablePages} pages</strong> you pick. Choose pages that show different layouts, tables, or edge cases.
          </p>
        </div>

        {/* Instructions */}
        <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-[0.75rem] leading-[1.5] bg-[#8B55DD]/[0.05] border border-[#8B55DD]/[0.15] text-[#5B21B6]">
          <Zap size={13} className="flex-shrink-0 mt-0.5" />
          <div>
            <strong>Pro tip:</strong> Include the first page (cover/header), middle pages (content variety), and last page (footer/end). This gives the AI the full picture.
          </div>
        </div>

        {/* Selection status */}
        <div className="text-[0.8125rem] font-medium text-[#3F3F46]">
          Selected: <span className="text-[#8B55DD]">{selectedPages.length} / {maxSelectablePages}</span>
        </div>

        {/* Page grid */}
        <div className="border border-black/[0.08] rounded-lg overflow-hidden">
          <div className="grid grid-cols-6 gap-px bg-black/[0.04] p-px">
            {allPages.map((pageNum) => {
              const isSelected = selectedPages.includes(pageNum);
              const isDisabled = !isSelected && !canSelectMore;
              return (
                <button
                  key={pageNum}
                  className={`aspect-square flex items-center justify-center text-[0.6875rem] font-bold rounded cursor-pointer transition-all duration-150 border-none ${
                    isSelected
                      ? "bg-[#8B55DD] text-white shadow-[0_2px_4px_rgba(139,85,221,0.2)]"
                      : isDisabled
                      ? "bg-[#F5F4F3] text-[#9CA3AF] cursor-not-allowed opacity-50"
                      : "bg-white text-[#52525B] hover:bg-[#8B55DD]/[0.06] hover:text-[#8B55DD]"
                  }`}
                  onClick={() => !isDisabled && handleTogglePage(pageNum)}
                  disabled={isDisabled}
                  title={`Page ${pageNum}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
        </div>

        {/* Quick actions */}
        <div className="flex gap-2">
          <button
            className={`${CLS.btn} ${CLS.btnGhost} ${CLS.btnSm}`}
            onClick={handleSelectAll}
            disabled={selectedPages.length === maxSelectablePages || isSubmitting}
          >
            Select first {maxSelectablePages}
          </button>
          <button
            className={`${CLS.btn} ${CLS.btnGhost} ${CLS.btnSm}`}
            onClick={handleClearAll}
            disabled={selectedPages.length === 0 || isSubmitting}
          >
            Clear
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-[0.8125rem] leading-[1.5] bg-[#D31C1C]/[0.06] border border-[#D31C1C]/20 text-[#991B1B]">
            <span className="flex-shrink-0">✗</span>
            <div>{error}</div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between">
          <button className={`${CLS.btn} ${CLS.btnGhost}`} onClick={onCancel} disabled={isSubmitting}>
            <ChevronUp size={13} /> Back
          </button>
          <button
            className={`${CLS.btn} ${CLS.btnPrimary}`}
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {isSubmitting
              ? <><Spinner size={14} /> Preparing…</>
              : <>Next step →</>
            }
          </button>
        </div>

      </div>
    </div>
  );
};

export default Step2SelectPages;
